"use client";

import {
  useParams,
  usePathname as useBrowserPathname,
  useRouter as useBrowserRouter,
} from "next/navigation";
import { useLocale } from "next-intl";
import Cookies from "js-cookie";
import { useEffect, useState, useTransition } from "react";
import { usePathname, useRouter } from "@/i18n/routing";
import { isLocale, resolveLocale, type Locale } from "@/i18n/config";
import setLocaleCookie from "@/lib/locale/setLocaleCookie";
import {
  LOCALE_COOKIE_NAME,
  LOCALE_TO_COOKIE_MAP,
  SPANISH_LOCALE_REWRITE_HEADER,
} from "@/lib/locale/constants";
import { useSpanishExperienceEnabled } from "@/lib/locale/useSpanishExperienceEnabled";
import {
  mirroredPath,
  normalizePath,
  readAlternatePath,
} from "@/lib/locale/hreflangAlternates";

/** Editorial Spanish pages: slugs are authored in Spanish, so no mirrored English route. */
const EDITORIAL_ES_PATTERN = /^\/es\/aprende\/[^/]+/;

const isEditorialSpanishWithoutTwin = (): boolean =>
  EDITORIAL_ES_PATTERN.test(window.location.pathname) &&
  readAlternatePath("en") === null;

const cookieToLocale = (value: string | undefined): Locale | null => {
  if (value === LOCALE_TO_COOKIE_MAP.es) return "es";
  if (value === LOCALE_TO_COOKIE_MAP.en) return "en";
  return null;
};

/** Bare `/` only — `/es` already encodes locale in the URL. */
const isBareHomepage = (pathname: string): boolean =>
  normalizePath(pathname) === "/";

/** CloudFront marked this response as a Spanish locale rewrite. */
const isLocaleRewriteActive = (): boolean =>
  document
    .querySelector(`meta[name="${SPANISH_LOCALE_REWRITE_HEADER}"]`)
    ?.getAttribute("content") === "1";

/**
 * Selector locale: on bare `/` with an active locale rewrite, use the
 * preference cookie (address bar stays `/` while content is Spanish).
 * Everywhere else — including `/` without rewrite and `/es` — use the URL.
 */
const resolveSelectorLocale = (
  urlLocale: Locale,
  browserPathname: string,
): Locale => {
  if (!isBareHomepage(browserPathname) || !isLocaleRewriteActive()) {
    return urlLocale;
  }

  return cookieToLocale(Cookies.get(LOCALE_COOKIE_NAME)) ?? urlLocale;
};

/** Current query as a next-intl `query`; repeated keys become arrays. */
const currentQuery = (): Record<string, string | string[]> | undefined => {
  const searchParams = new URLSearchParams(window.location.search);
  const query: Record<string, string | string[]> = {};
  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    query[key] = values.length > 1 ? values : values[0];
  }
  return Object.keys(query).length > 0 ? query : undefined;
};

export function useLocaleSwitch() {
  const router = useRouter();
  const browserRouter = useBrowserRouter();
  const pathname = usePathname();
  const browserPathname = useBrowserPathname();
  const params = useParams();
  const [isPending, startTransition] = useTransition();
  const { isEnabled: isSpanishEnabled } = useSpanishExperienceEnabled();

  const intlLocale = useLocale();
  const localeParam = typeof params?.locale === "string" ? params.locale : "";
  const urlLocale = resolveLocale(
    isLocale(localeParam) ? localeParam : intlLocale,
  );

  const [locale, setLocale] = useState<Locale>(() =>
    resolveSelectorLocale(urlLocale, browserPathname),
  );

  const [isHidden, setIsHidden] = useState<boolean>(() =>
    isEditorialSpanishWithoutTwin(),
  );

  useEffect(() => {
    setLocale(resolveSelectorLocale(urlLocale, browserPathname));
    setIsHidden(isEditorialSpanishWithoutTwin());
  }, [browserPathname, urlLocale]);

  const switchLocale = (newLocale: string) => {
    if (
      !isLocale(newLocale) ||
      newLocale === locale ||
      isPending ||
      (newLocale === "es" && !isSpanishEnabled)
    ) {
      return;
    }

    // Preference cookie updates only on explicit selection.
    void setLocaleCookie(newLocale);
    setLocale(newLocale);
    startTransition(() => {
      const alternatePath = readAlternatePath(newLocale);
      // Pages without a Spanish alternate render the same template in both
      // locales; their `en` alternate may point at a different page.
      const hasSpanishAlternate = readAlternatePath("es") !== null;

      // Slug differs across locales; the mirrored route would 404, so follow hreflang.
      if (
        alternatePath &&
        hasSpanishAlternate &&
        normalizePath(alternatePath) !==
          normalizePath(mirroredPath(window.location.pathname, newLocale))
      ) {
        browserRouter.replace(`${alternatePath}${window.location.search}`);
        return;
      }

      const query = currentQuery();
      router.replace(
        // @ts-expect-error -- TypeScript validates known params for the pathname.
        query ? { pathname, params, query } : { pathname, params },
        { locale: newLocale },
      );
    });
  };

  return {
    locale,
    isPending,
    /** Editorial Spanish pages without an English twin cannot offer a locale switch. */
    isHidden,
    switchLocale,
  };
}
